import React, { Dispatch, SetStateAction, useState } from 'react';

import { Button, Flex, Tooltip } from '@mantine/core';
import { IconPhone, IconPhoneEnd } from '@tabler/icons-react';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { LiveKitRoomWrapper } from 'components/TalkWithAgentButton/LiveKitRoomWrapper';
import { LiveKitVoiceAgentSession } from 'components/TalkWithAgentButton/LiveKitVoiceAgentSession';
import { useTranslation } from 'react-i18next';
import { GuestSmartAssistantService } from 'services/guest-smart-assistant-service';
import { NotificationService } from 'services/notification-service';
import { SmartAssistantId } from 'shared/interfaces/livekit/CreateAccessToken';

export interface GuestTalkWithAgentButtonProps {
  smartAssistantId: SmartAssistantId;
  disabledTooltipLabel?: string;
  setCurrentCombinedVolume: Dispatch<SetStateAction<number>>;
  setIsCallActive: Dispatch<SetStateAction<boolean>>;
}

export function GuestTalkWithAgentButton({
  smartAssistantId,
  disabledTooltipLabel,
  setCurrentCombinedVolume,
  setIsCallActive
}: GuestTalkWithAgentButtonProps) {
  const { t } = useTranslation();
  const [isSessionLoading, setIsSessionLoading] = useState(false);

  const createGestAccessTokenMutation = useMutation({
    onMutate: () => setIsSessionLoading(true),
    mutationFn: () => GuestSmartAssistantService.createGuestAccessTokenForNewRoom({ smartAssistantId }),
    onError: (error: AxiosError) => {
      NotificationService.showResult(false, 'FEED_BACK.SMART_ASSISTANT.FAILURE.TALK_WITH_AGENT', error);
      setIsSessionLoading(false);
    }
  });

  const endSession = () => {
    setIsSessionLoading(false);
    createGestAccessTokenMutation.reset();
    setIsCallActive(false);
  };

  const liveKitToken = createGestAccessTokenMutation.data;
  const isCallActive = !!liveKitToken && !isSessionLoading;

  // Update parent's isCallActive state when local state changes
  React.useEffect(() => {
    setIsCallActive(isCallActive);
  }, [isCallActive, setIsCallActive]);

  return (
    <>
      <Tooltip w={250} multiline label={disabledTooltipLabel} disabled={!disabledTooltipLabel}>
        <Flex direction="column" gap="lg">
          <Button
            onClick={() => (createGestAccessTokenMutation.data ? endSession() : createGestAccessTokenMutation.mutate())}
            disabled={!!disabledTooltipLabel}
            loading={createGestAccessTokenMutation.isLoading || isSessionLoading}
            color={isCallActive ? 'red' : 'black'}
            radius="md"
            leftIcon={isCallActive ? <IconPhoneEnd size={16} /> : <IconPhone size={16} />}>
            {isCallActive
              ? t('SMART_ASSISTANT.PHONE_AGENT_BASICS_SECTION.TALK_WITH_AGENT_BUTTON.END_CALL')
              : t('SMART_ASSISTANT.PHONE_AGENT_BASICS_SECTION.TALK_WITH_AGENT_BUTTON.TALK_WITH_AGENT')}
          </Button>
        </Flex>
      </Tooltip>

      {liveKitToken && (
        <LiveKitRoomWrapper token={liveKitToken}>
          <LiveKitVoiceAgentSession
            setIsLoading={setIsSessionLoading}
            onSessionEnd={endSession}
            setCurrentCombinedVolume={setCurrentCombinedVolume}
          />
        </LiveKitRoomWrapper>
      )}
    </>
  );
}
